import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  AlertCircle,
  Clock,
  ChevronRight,
  User,
  Phone,
  Mail,
  FileText,
  Briefcase,
  Loader2,
  ArrowLeft,
  Download,
  CheckCircle,
  Heart
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import essService from '../../api/services/essService';
import { formatDate } from '../../utils/stringUtils';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ResignationForm from './ResignationForm';

const SubmitResignation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState('form'); // 'form', 'list', or 'detail'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [withdrawModal, setWithdrawModal] = useState({ show: false, requestId: null, reason: '' });

  // React Query: Fetch requests
  const { data: requestsData, isLoading: requestsLoading, refetch: refetchRequests } = useQuery({
    queryKey: ['resignationRequests'],
    queryFn: async () => {
      const res = await essService.getResignationRequests();
      if (!res.success) throw new Error(res.error || 'Failed to fetch requests');
      return res.data || [];
    }
  });

  const requests = requestsData || [];

  // Update view mode when requests data changes (if already submitted, default to list view)
  useEffect(() => {
    if (requests.length > 0) {
      const latest = requests[0];
      if (latest.status !== 'Draft' && viewMode === 'form') {
        setViewMode('list');
      }
    }
  }, [requestsData]);
  // Keep selectedRequest in sync with updated data from requests list
  useEffect(() => {
    if (selectedRequest && requests.length > 0) {
      const updated = requests.find(r => (r._id || r.requestId) === (selectedRequest._id || selectedRequest.requestId));
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedRequest)) {
        setSelectedRequest(updated);
      }
    }
  }, [requests, selectedRequest]);

  const sentenceCase = (text) => {
    if (!text) return '';
    const trimmed = text.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  };


  const handleWithdraw = (id) => {
    setWithdrawModal({ show: true, requestId: id, reason: '' });
  };

  // React Query: Withdraw Mutation
  const withdrawResignationMutation = useMutation({
    mutationFn: ({ id, reason }) => essService.deleteResignationRequest(id, reason),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Request withdrawn successfully');
        setWithdrawModal({ show: false, requestId: null, reason: '' });
        queryClient.invalidateQueries({ queryKey: ['resignationRequests'] });
        if (viewMode === 'detail') setViewMode('list');
      } else {
        toast.error(res.error || 'Failed to withdraw request');
      }
    },
    onError: (error) => {
      toast.error('Error withdrawing request');
    }
  });

  const confirmWithdraw = () => {
    if (!withdrawModal.reason.trim()) {
      toast.error('Please provide a reason for withdrawal');
      return;
    }
    withdrawResignationMutation.mutate({
      id: withdrawModal.requestId,
      reason: withdrawModal.reason
    });
  };

  const StatusBadge = ({ status, type = 'default' }) => {
    const styles = {
      default: 'bg-gray-100 text-gray-800',
      success: 'bg-emerald-100 text-emerald-800',
      warning: 'bg-amber-100 text-amber-800',
      info: 'bg-blue-100 text-blue-800',
      error: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      withdrawn: 'bg-gray-100 text-gray-400 border-gray-200',
      withdrawed: 'bg-gray-100 text-gray-400 border-gray-200'
    };

    const statusType = status?.toLowerCase() || 'default';
    const finalStyle = styles[statusType] || styles[type] || styles.default;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${finalStyle}`}>
        {status?.toLowerCase() || 'pending'}
      </span>
    );
  };

  const SectionTitle = ({ icon: Icon, title, description }) => (
    <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-100">
      <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-sm text-gray-500">{description}</p>}
      </div>
    </div>
  );

  const LabelTag = ({ text, type = 'manual' }) => {
    const styles = {
      manual: 'bg-amber-100 text-amber-800 border-amber-200',
      auto: 'bg-blue-100 text-blue-800 border-blue-200',
      system: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      mandatory: 'bg-red-50 text-red-700 border-red-100'
    };

    return (
      <span className={`ml-2 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${styles[type] || styles.manual}`}>
        {text}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-8">

        {/* Header Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full blur-3xl -mr-16 -mt-16 opacity-50" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Exit Management</h1>
              <p className="mt-2 text-gray-600">Initiate or track your resignation process.</p>
            </div>
            <div className="flex items-center bg-gray-50 p-1 rounded-xl border border-gray-200 self-start">
              <button
                onClick={() => setViewMode('form')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'form' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Submit New
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                My History {requests.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-md text-[10px]">{requests.length}</span>}
              </button>
            </div>
          </div>
        </div>

        {requestsLoading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm animate-pulse">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading your resignation details...</p>
          </div>
        ) : viewMode === 'form' ? (
          <ResignationForm
            requests={requests}
            onSuccess={() => setViewMode('list')}
          />
        ) : viewMode === 'detail' && selectedRequest ? (
          /* Full Page Detail View */
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Detail Header / Nav */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center justify-between">
              <button
                onClick={() => {
                  setViewMode('list');
                  setSelectedRequest(null);
                }}
                className="flex items-center text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors group"
              >
                <div className="p-2 bg-gray-50 rounded-lg mr-3 group-hover:bg-blue-50">
                  <ArrowLeft className="w-4 h-4" />
                </div>
                Back to History
              </button>
              <div className="flex items-center space-x-3">
                <span className="text-xs text-gray-400 font-mono">Reference: {selectedRequest.requestId}</span>
                <StatusBadge status={selectedRequest.status} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Primary Content Card */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-transparent text-gray-900">
                    <h2 className="text-2xl font-bold mb-1 capitalize">{selectedRequest.reason}</h2>
                    <p className="text-gray-500 text-sm capitalize">{selectedRequest.resignationCategory} • <span className="normal-case text-gray-400 font-medium">Submitted on {formatDate(selectedRequest.createdAt)}</span></p>
                  </div>

                  <div className="p-8 space-y-10">
                    <div className="grid gap-8">
                      <div>
                        <h4 className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-4 flex items-center">
                          <FileText className="w-3.5 h-3.5 mr-2" />
                          Detailed Statement
                        </h4>
                        <div className="text-gray-700 leading-relaxed bg-blue-50/30 p-6 rounded-2xl border border-blue-100/50 break-all">
                          {sentenceCase(selectedRequest.detailedReason) || "No additional comments provided."}
                        </div>
                      </div>

                      {selectedRequest.handoverNotes && (
                        <div>
                          <h4 className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em] mb-4 flex items-center">
                            <Briefcase className="w-3.5 h-3.5 mr-2" />
                            Handover Notes
                          </h4>
                          <div className="text-gray-700 leading-relaxed bg-emerald-50/20 p-6 rounded-2xl border border-emerald-100/50 break-all">
                            {sentenceCase(selectedRequest.handoverNotes)}
                          </div>
                        </div>
                      )}

                      {selectedRequest.status?.toLowerCase() === 'withdrawed' && selectedRequest.withdrawal?.remark && (
                        <div className="animate-in slide-in-from-top-4 duration-500">
                          <h4 className="text-[10px] font-bold text-rose-400 uppercase tracking-[0.2em] mb-4 flex items-center">
                            <AlertCircle className="w-3.5 h-3.5 mr-2" />
                            Withdrawal Remark
                          </h4>
                          <div className="text-rose-700 leading-relaxed bg-rose-50/50 p-6 rounded-2xl border border-rose-100/50 break-all">
                            {sentenceCase(selectedRequest.withdrawal.remark)}
                            {selectedRequest.withdrawal.timestamp && (
                              <p className="text-[11px] text-rose-400 mt-3 font-medium flex items-center">
                                <Clock className="w-3 h-3 mr-1.5" />
                                Withdrawn on {formatDate(selectedRequest.withdrawal.timestamp)}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Timeline Grid */}
                    <div className="pt-10 border-t border-gray-100">
                      <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">Timeline & Contractual Info</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Notice Period</p>
                          <p className="font-semibold text-gray-900">{selectedRequest.noticePeriod} Days</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-xs text-gray-500 mb-1">Notice End Date</p>
                          <p className="font-semibold text-gray-900">{selectedRequest.noticeEndDate ? formatDate(selectedRequest.noticeEndDate, { includeTime: false }) : 'N/A'}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl border-l-2 border-blue-500">
                          <p className="text-xs text-gray-500 mb-1">Proposed Last Date</p>
                          <p className="font-semibold text-blue-600">{formatDate(selectedRequest.proposedLastWorkingDate, { includeTime: false })}</p>
                        </div>
                      </div>
                    </div>

                    {/* Buyout Section */}
                    {selectedRequest.buyoutRequest === 'Yes' && (
                      <div className="p-6 bg-amber-50 rounded-2xl border border-amber-100">
                        <div className="flex items-start space-x-4">
                          <div className="p-2 bg-amber-100 rounded-lg text-amber-600">
                            <AlertCircle className="w-5 h-5" />
                          </div>
                          <div>
                            <h5 className="font-bold text-amber-900">Buyout Request Initiated</h5>
                            <p className="text-sm text-amber-700 mt-1 break-all">Reason: {sentenceCase(selectedRequest.buyoutReason) || "No specific reason given."}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Personal Attestations */}
                    <div className="pt-10 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                          <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">Employee Declarations</h4>
                          <div className="grid grid-cols-1 gap-4">
                            {[
                              { label: 'Voluntary Separation', active: selectedRequest.declarations?.confirmVoluntary },
                              { label: 'Policy Acceptance', active: selectedRequest.declarations?.confirmPolicy },
                              { label: 'Accuracy Check', active: selectedRequest.declarations?.confirmAccurate },
                              { label: 'Exit Interview Opt-in', active: selectedRequest.willingToParticipateInExitInterview }
                            ].map((item, idx) => (
                              <div key={idx} className="flex items-center p-3 bg-gray-50/50 rounded-lg border border-gray-100">
                                {item.active ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-3" />
                                ) : (
                                  <div className="w-4 h-4 rounded-full border border-gray-200 mr-3" />
                                )}
                                <span className="text-sm text-gray-600">{item.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-6">Attached Documents</h4>
                            <div className="space-y-3">
                              {selectedRequest.attachments.map((file, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-blue-50/30 rounded-xl border border-blue-100/50">
                                  <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                      <FileText className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">
                                        {file.originalName || 'Document'}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                    </div>
                                  </div>
                                  <a
                                    href={file.filePath}
                                    download={file.originalName}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Download Document"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* AUDIT TIMELINE */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 rounded-full -mr-16 -mt-16 opacity-50 blur-3xl"></div>
                  <div className="p-8 pb-4">
                    <SectionTitle
                      icon={Clock}
                      title="Audit Timeline & History"
                      description="Track the real-time progress of your resignation process."
                    />
                  </div>
                  <div className="p-8 pt-0 relative ml-6 md:ml-10">
                    <div className="relative pl-8 space-y-8 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100">
                      {/* Draft Created */}
                      <div className="relative">
                        <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-gray-300 shadow-sm flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                        </div>
                        <div className="bg-gray-50/50 p-3 rounded-xl border border-gray-100 inline-block min-w-[240px]">
                          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">Process Initiated</p>
                          <p className="text-sm text-gray-900 font-semibold mb-0.5">Resignation Draft Created</p>
                          <p className="text-[10px] text-gray-400 font-medium">{formatDate(selectedRequest.createdAt)}</p>
                        </div>
                      </div>

                      {/* Submitted */}
                      <div className="relative">
                        <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-blue-500 shadow-sm flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        </div>
                        <div className="bg-blue-50/30 p-3 rounded-xl border border-blue-100/50 inline-block min-w-[240px]">
                          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Submission</p>
                          <p className="text-sm text-gray-900 font-semibold mb-0.5">Application Submitted</p>
                          <p className="text-[10px] text-gray-500 font-medium">{formatDate(selectedRequest.submittedDate || selectedRequest.createdAt)}</p>
                        </div>
                      </div>

                      {/* Manager Action */}
                      {selectedRequest.managerApproval?.timestamp && (
                        <div className="relative">
                          <div className={`absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 ${selectedRequest.managerApproval.approvalStatus === 'rejected' ? 'border-rose-500' : 'border-emerald-500'} shadow-sm flex items-center justify-center`}>
                            <div className={`w-1.5 h-1.5 ${selectedRequest.managerApproval.approvalStatus === 'rejected' ? 'bg-rose-500' : 'bg-emerald-500'} rounded-full`}></div>
                          </div>
                          <div className={`${selectedRequest.managerApproval.approvalStatus === 'rejected' ? 'bg-rose-50/30 border-rose-100/50' : 'bg-emerald-50/30 border-emerald-100/50'} p-3 rounded-xl border inline-block min-w-[240px]`}>
                            <p className={`text-[10px] font-bold ${selectedRequest.managerApproval.approvalStatus === 'rejected' ? 'text-rose-600' : 'text-emerald-600'} uppercase tracking-widest mb-1`}>Manager Review</p>
                            <p className="text-sm text-gray-900 font-semibold mb-0.5">
                              Request {selectedRequest.managerApproval.approvalStatus === 'rejected' ? 'Rejected' : 'Approved'}
                            </p>
                            <p className="text-[11px] text-gray-600 font-medium">By {selectedRequest.managerApproval.name || 'Reporting Manager'}</p>
                            <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(selectedRequest.managerApproval.timestamp)}</p>
                          </div>
                        </div>
                      )}

                      {/* HR Action */}
                      {selectedRequest.hrApproval?.timestamp && (
                        <div className="relative">
                          <div className={`absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 ${selectedRequest.hrApproval.approvalStatus === 'rejected' ? 'border-rose-500' : 'border-purple-500'} shadow-sm flex items-center justify-center`}>
                            <div className={`w-1.5 h-1.5 ${selectedRequest.hrApproval.approvalStatus === 'rejected' ? 'bg-rose-500' : 'bg-purple-500'} rounded-full`}></div>
                          </div>
                          <div className={`${selectedRequest.hrApproval.approvalStatus === 'rejected' ? 'bg-rose-50/30 border-rose-100/50' : 'bg-purple-50/30 border-purple-100/50'} p-3 rounded-xl border inline-block min-w-[240px]`}>
                            <p className={`text-[10px] font-bold ${selectedRequest.hrApproval.approvalStatus === 'rejected' ? 'text-rose-600' : 'text-purple-600'} uppercase tracking-widest mb-1`}>HR Compliance</p>
                            <p className="text-sm text-gray-900 font-semibold mb-0.5">
                              Final Action: {selectedRequest.hrApproval.approvalStatus === 'rejected' ? 'Rejected' : 'Approved'}
                            </p>
                            <p className="text-[11px] text-gray-600 font-medium">By {selectedRequest.hrApproval.name || 'HR Manager'}</p>
                            <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(selectedRequest.hrApproval.timestamp)}</p>
                          </div>
                        </div>
                      )}

                      {/* Withdrawal Permission */}
                      {selectedRequest.allowWithdrawal && !selectedRequest.withdrawal?.timestamp && (
                        <div className="relative">
                          <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-amber-500 shadow-sm flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></div>
                          </div>
                          <div className="bg-amber-50/30 p-3 rounded-xl border border-amber-100/50 inline-block min-w-[240px]">
                            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Withdrawal Control</p>
                            <p className="text-sm text-gray-900 font-semibold mb-0.5">Withdrawal Option Enabled</p>
                            <p className="text-[10px] text-gray-500 font-medium leading-relaxed max-w-[300px]">Management has granted permission for you to withdraw this resignation request.</p>
                          </div>
                        </div>
                      )}

                      {/* Withdrawn */}
                      {selectedRequest.withdrawal?.timestamp && (
                        <div className="relative">
                          <div className="absolute -left-[37px] top-1.5 w-4 h-4 rounded-full bg-white border-2 border-slate-500 shadow-sm flex items-center justify-center">
                            <div className="w-1.5 h-1.5 bg-slate-500 rounded-full"></div>
                          </div>
                          <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 inline-block min-w-[240px]">
                            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest mb-1">Final Status</p>
                            <p className="text-sm text-gray-900 font-semibold mb-0.5">Resignation Withdrawn</p>
                            {selectedRequest.withdrawal.remark && (
                              <p className="text-[11px] text-slate-500 italic mt-1 max-w-[250px] break-words">"{selectedRequest.withdrawal.remark}"</p>
                            )}
                            <p className="text-[10px] text-gray-400 font-medium mt-1">{formatDate(selectedRequest.withdrawal.timestamp)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-6 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
                    Approval Status
                  </h4>
                  <div className="space-y-6">
                    {/* Manager Approval */}
                    <div className="relative pl-8 pb-2 border-l-2 border-gray-100">
                      <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 ${selectedRequest.managerApproval?.approvalStatus === 'approved' ? 'border-emerald-500' :
                        selectedRequest.managerApproval?.approvalStatus === 'rejected' ? 'border-red-500' :
                          'border-blue-500'
                        }`} />
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Manager Approval</p>
                          <div className="mt-1">
                            <StatusBadge status={selectedRequest.managerApproval?.approvalStatus || 'pending'} />
                          </div>
                        </div>

                        {(selectedRequest.managerApproval?.approvalStatus && selectedRequest.managerApproval?.approvalStatus !== 'pending') && (
                          <div className="bg-gray-50/50 p-3 rounded-lg border border-gray-100 space-y-2">
                            {selectedRequest.managerApproval?.name && (
                              <p className="text-[10px] text-gray-600 font-semibold mb-1">
                                Reviewed by: <span className="text-gray-900">{selectedRequest.managerApproval.name}</span>
                              </p>
                            )}

                            {(selectedRequest.managerApproval?.approvalReason || selectedRequest.managerApproval?.rejectedReason) && (
                              <p className="text-xs text-gray-500 italic leading-relaxed break-words whitespace-pre-wrap bg-white p-2 rounded border border-gray-50 tracking-tight font-medium">
                                "{selectedRequest.managerApproval.approvalReason || selectedRequest.managerApproval.rejectedReason}"
                              </p>
                            )}

                            {selectedRequest.managerApproval?.timestamp && (
                              <p className="text-[10px] text-gray-400 flex items-center">
                                <Clock className="w-3 h-3 mr-1" /> {formatDate(selectedRequest.managerApproval.timestamp)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* HR Approval */}
                    <div className="relative pl-8 pb-0">
                      <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-white border-2 ${selectedRequest.hrApproval?.approvalStatus === 'approved' ? 'border-emerald-500' :
                        selectedRequest.hrApproval?.approvalStatus === 'rejected' ? 'border-red-500' :
                          'border-gray-200'
                        }`} />
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">HR Approval</p>
                          <div className="mt-1">
                            <StatusBadge status={selectedRequest.hrApproval?.approvalStatus || 'pending'} />
                          </div>
                        </div>

                        {(selectedRequest.hrApproval?.approvalStatus && selectedRequest.hrApproval?.approvalStatus !== 'pending') && (
                          <div className="bg-gray-50/50 p-3 rounded-lg border border-gray-100 space-y-2">
                            {selectedRequest.hrApproval?.name && (
                              <p className="text-[10px] text-gray-600 font-semibold mb-1">
                                Processed by: <span className="text-gray-900">{selectedRequest.hrApproval.name}</span>
                              </p>
                            )}

                            {(selectedRequest.hrApproval?.approvalReason || selectedRequest.hrApproval?.rejectedReason) && (
                              <p className="text-xs text-gray-500 italic leading-relaxed break-words whitespace-pre-wrap bg-white p-2 rounded border border-gray-50 tracking-tight font-medium">
                                "{selectedRequest.hrApproval.approvalReason || selectedRequest.hrApproval.rejectedReason}"
                              </p>
                            )}

                            {selectedRequest.hrApproval?.timestamp && (
                              <p className="text-[10px] text-gray-400 flex items-center">
                                <Clock className="w-3 h-3 mr-1" /> {formatDate(selectedRequest.hrApproval.timestamp)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-6 flex items-center">
                    <Heart className="w-4 h-4 mr-2 text-rose-500" />
                    Emergency Contact
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Contact Name</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5 capitalize break-all">{selectedRequest.emergencyContact?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Communication</p>
                      <div className="mt-1 flex flex-col space-y-1">
                        <span className="text-sm text-gray-600 flex items-center text-xs">
                          <Phone className="w-3.5 h-3.5 mr-2" /> {selectedRequest.emergencyContact?.phone}
                        </span>
                        {selectedRequest.emergencyContact?.email && (
                          <span className="text-sm text-gray-600 flex items-center text-xs break-all">
                            <Mail className="w-3.5 h-3.5 mr-2 flex-shrink-0" /> {selectedRequest.emergencyContact?.email.toLowerCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Relation</p>
                      <p className="text-sm font-medium text-gray-600 mt-1 capitalize break-all">{selectedRequest.emergencyContact?.relationship}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  {(!['withdrawn', 'withdrawed', 'completed'].includes(selectedRequest.status?.toLowerCase())) &&
                    (!!selectedRequest.allowWithdrawal || ['pending', 'draft'].includes(selectedRequest.status?.toLowerCase())) && (
                      <button
                        onClick={() => {
                          handleWithdraw(selectedRequest._id);
                          setViewMode('list');
                          setSelectedRequest(null);
                        }}
                        className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-all border border-red-100"
                      >
                        Withdraw Request
                      </button>
                    )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* List View History */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="font-bold text-gray-900 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-500" />
                Resignation History
              </h3>
              <span className="text-xs font-medium text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-200">
                Total {requests.length} Requests
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                    <th className="px-6 py-4">Request ID</th>
                    <th className="px-6 py-4">Submit Date</th>
                    <th className="px-6 py-4">Proposed Last Date</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Overall Status</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requests.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No resignation history found.
                      </td>
                    </tr>
                  ) : (
                    requests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-mono text-xs font-bold text-blue-600">{request.requestId}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(request.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {request.proposedLastWorkingDate ? formatDate(request.proposedLastWorkingDate, { includeTime: false }) : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600 border border-gray-200 capitalize">
                            {request.resignationCategory}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge status={request.status} />
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setViewMode('detail');
                            }}
                            className="p-1 px-3 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition-all"
                          >
                            View
                          </button>
                          {(!['withdrawn', 'withdrawed', 'completed'].includes(request.status?.toLowerCase())) &&
                            (!!request.allowWithdrawal || ['pending', 'draft'].includes(request.status?.toLowerCase())) && (
                              <button
                                onClick={() => handleWithdraw(request._id)}
                                className="p-1 px-3 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-all"
                              >
                                Withdraw
                              </button>
                            )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Withdrawal Reason Modal */}
        {withdrawModal.show && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-300 !mt-0">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl scale-in-center overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <h4 className="font-bold text-gray-900 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-rose-500" />
                  Withdraw Resignation
                </h4>
                <button
                  onClick={() => setWithdrawModal({ show: false, requestId: null, reason: '' })}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <AlertCircle className="w-5 h-5 rotate-45" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 italic">
                  "Please let us know why you are choosing to stay. We value your presence in the team!"
                </p>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Withdrawal Reason *</label>
                  <div className="relative">
                    <textarea
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm h-32 resize-none"
                      placeholder="E.g., Decision changed, received internal promotion, personal reasons resolved..."
                      value={withdrawModal.reason}
                      maxLength={500}
                      onChange={(e) => {
                        const val = e.target.value;
                        const cleanVal = val.replace(/[^a-zA-Z0-9\s.,?!'()\-#$@:;]/g, '');
                        setWithdrawModal(prev => ({ ...prev, reason: cleanVal }));
                      }}
                    />
                    <div className="absolute bottom-2 right-3 text-xs text-gray-400 pointer-events-none">
                      {withdrawModal.reason.length}/500
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setWithdrawModal({ show: false, requestId: null, reason: '' })}
                  className="flex-1 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
                >
                  Go Back
                </button>
                <button
                  onClick={confirmWithdraw}
                  disabled={withdrawResignationMutation.isPending || !withdrawModal.reason.trim()}
                  className="flex-1 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-semibold hover:bg-rose-700 transition-all shadow-lg shadow-rose-200 disabled:opacity-50"
                >
                  {withdrawResignationMutation.isPending ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </div>
                  ) : 'Confirm Withdrawal'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SubmitResignation;
